import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Compass, FileText, Home, Languages, MessageCircleQuestion, Pin, Users, Wallet, type LucideIcon } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { initials } from "@/data/buddies";
import type { TopicId } from "@/data/community";
import type { Author } from "@/data/community-feed";

/** Where thread links point: the public page, or the community inside the app. */
export const CommunityBase = createContext("/community");
export const useCommunityBase = () => useContext(CommunityBase);

export const C = {
  INK: "#0A0F0E",
  BRAND_DARK: "#00332F",
  TEAL: "#2EE6D6",
  TEAL_DEEP: "#00786F",
  TEAL_SOFT: "#CFF9F4",
  OAK: "#C6872F",
  OAK_SOFT: "#F6E7D2",
  SECONDARY: "#46504D",
  MUTED: "#69716E",
  PAPER: "#FBFBF8",
  PANEL: "#F2F2ED",
  RULE: "#E0E3DE",
  SIGNAL: "#BF4B22",
} as const;

export const TOPIC_ICON: Record<TopicId, LucideIcon> = {
  route: Compass,
  documents: FileText,
  money: Wallet,
  german: Languages,
  living: Home,
  parents: Users,
};

/** "38 min", "5 h", "3 d", then the date. */
export function timeAgo(iso: string): string {
  const then = new Date(iso);
  const mins = Math.max(0, Math.round((Date.now() - then.getTime()) / 60_000));
  if (mins < 60) return mins <= 1 ? "just now" : `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d`;
  return then.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const SIZES = { sm: 32, md: 40, lg: 48 } as const;

/** A face for every kind of author. Team posts carry the Migrada mark. */
export function Avatar({ author, size = "md", plain = false }: { author: Author; size?: keyof typeof SIZES; /** No ring and no flag, for tight stacks. */ plain?: boolean }) {
  const px = SIZES[size];
  const font = Math.round(px * 0.36);
  let inner: React.ReactNode;
  let bg: string = C.PANEL;
  let fg: string = C.BRAND_DARK;
  let ring = "transparent";

  // The team posts as Migrada itself: the real mark on its square tile, set
  // apart from the round faces of people.
  if (author.kind === "team") return <LogoMark size={px} className="rounded-[28%]" />;

  if (author.kind === "common") {
    bg = C.OAK_SOFT;
    fg = C.OAK;
    inner = <MessageCircleQuestion style={{ width: px * 0.5, height: px * 0.5 }} strokeWidth={2.2} />;
  } else {
    if (author.kind === "buddy") {
      bg = C.TEAL_SOFT;
      if (!plain) ring = C.TEAL;
    }
    inner = <span className="font-black" style={{ fontSize: font }}>{initials(author.name)}</span>;
  }

  return (
    <span className="relative inline-flex shrink-0" aria-hidden="true">
      <span
        className="flex items-center justify-center rounded-full"
        style={{ width: px, height: px, backgroundColor: bg, color: fg, boxShadow: `0 0 0 2px ${ring === "transparent" ? "transparent" : "#FFFFFF"}, 0 0 0 4px ${ring}` }}
      >
        {inner}
      </span>
      {!plain && author.countryCode && (author.kind === "buddy" || author.kind === "member") && (
        <span className="absolute -bottom-1 -right-1 rounded-full bg-white p-[2px]">
          <CountryFlag code={author.countryCode} size="sm" circle animate={false} className="!h-4 !w-4" />
        </span>
      )}
    </span>
  );
}

/**
 * A buddy says so in words next to the name, the way people sign off. The
 * team needs nothing here: the Migrada mark already says who is talking.
 */
export function KindBadge({ kind }: { kind: Author["kind"] }) {
  if (kind !== "buddy") return null;
  return (
    <span className="text-[13px] font-bold" style={{ color: C.TEAL_DEEP }}>
      Buddy
    </span>
  );
}

/** A quiet marker for the post the team keeps at the top. */
export function PinnedMark() {
  return (
    <span className="flex shrink-0 items-center gap-1 text-[13px] font-semibold" style={{ color: C.MUTED }}>
      <Pin className="h-3.5 w-3.5 rotate-45" aria-hidden="true" />
      Pinned
    </span>
  );
}

export function SampleTag() {
  return (
    <span
      className="rounded-full border border-dashed px-2 py-[1px] text-[11px] font-bold uppercase tracking-[0.06em]"
      style={{ borderColor: C.MUTED, color: C.MUTED }}
      title="Fictional sample content, shown in development only"
    >
      Sample
    </span>
  );
}

/** Name, badge, detail and time, in one tidy block next to an avatar. */
export function AuthorLine({ author, postedAt, sample }: { author: Author; postedAt: string; sample?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="truncate text-[15px] font-extrabold tracking-[-0.01em]" style={{ color: C.INK }}>
          {author.name}
        </span>
        <KindBadge kind={author.kind} />
        {sample && <SampleTag />}
      </div>
      <p className="truncate text-[13px]" style={{ color: C.MUTED }}>
        {author.detail ? `${author.detail} · ` : ""}
        <time dateTime={postedAt}>{timeAgo(postedAt)}</time>
      </p>
    </div>
  );
}

const SAVED_KEY = "migrada:saved-threads";

/** Threads this visitor bookmarked. Stays in their browser only. */
export function useSavedThreads(): [Set<string>, (slug: string) => void] {
  const [saved, setSaved] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SAVED_KEY);
      if (raw) setSaved(new Set(JSON.parse(raw) as string[]));
    } catch {} // Private mode or blocked storage: start empty
  }, []);

  const toggle = useCallback((slug: string) => {
    setSaved((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      try {
        window.localStorage.setItem(SAVED_KEY, JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  }, []);

  return [saved, toggle];
}

export interface Member {
  email: string | null;
}

/**
 * Whether a member is signed in on this device, read from the stored Supabase
 * session (the same check the homepage uses). Public pages do not load
 * Supabase until the auth modal opens, so this stays synchronous and cheap.
 * It is a convenience for the interface, not a security check: anything that
 * must be members-only is enforced by the database once posting moves there.
 */
export function readMember(): Member | null {
  try {
    const key = Object.keys(window.localStorage).find((k) => k.startsWith("sb-") && k.endsWith("-auth-token"));
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed?.access_token) return null;
    return { email: parsed?.user?.email ?? null };
  } catch {
    return null;
  }
}

export function useMember(): Member | null {
  const [member, setMember] = useState<Member | null>(null);
  useEffect(() => {
    const update = () => setMember(readMember());
    update();
    window.addEventListener("storage", update);
    window.addEventListener("focus", update);
    // The auth modal signs in within this tab, which fires no storage event.
    const t = window.setInterval(update, 1500);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("focus", update);
      window.clearInterval(t);
    };
  }, []);
  return member;
}

const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

/** "[r/MoveToEurope](https://…)" → "r/MoveToEurope", for previews, search and JSON-LD. */
export function plainText(text: string): string {
  return text.replace(LINK_RE, "$1");
}

/** Renders the one inline format threads support: [label](https://…) links, opened in a new tab. */
export function RichText({ text }: { text: string }) {
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of Array.from(text.matchAll(LINK_RE))) {
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    out.push(
      <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer" className="relative z-10 font-bold underline decoration-[#2EE6D6] decoration-2 underline-offset-4" style={{ color: C.BRAND_DARK }}>
        {m[1]}
      </a>,
    );
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}
